import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow, Table, TableBody } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageProductTable from '../tabels/StageProductTable';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey, red, green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import theme from 'assets/theme';
import ProductList from './ProductList';

import { useVisibility } from 'context/visibilityContext'; // Import the hook
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter'; // Import the filter utility
import DiffRenderer from './DiffRenderer';

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  '&:hover > *': {
    backgroundColor: theme.palette.action.hover,
  },
  ...(selected && {
    '& > *': {
      // Keep background transparent or slightly highlighted on select if needed
      // backgroundColor: theme.palette.action.selected,
    },
  }),
}));

function ProductRulesReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null); // For main rules
  const [productDataMap, setProductDataMap] = useState({});
  const [expandedNestedItems, setExpandedNestedItems] = useState({}); // For actions, lookups, conditions
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { visibilityConfig, isLoadingConfig, configError } = useVisibility(); // Get context values

  const ruleColumns = [
    { name: 'rule', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  // Helper to generate display name for nested items
  const getNestedItemDisplayName = (item, itemCategory) => {
    const innerItem = item[itemCategory] || item; // Handle potential nesting like { productActions: {...} }
    const fields = innerItem?.fields || {};
    switch (itemCategory) {
      case 'productActions':
        return `Action: ${fields.SBQQ__Type__c} ${item.productReference ? `(${item.productReference})` : ''} ${
          fields.SBQQ__FilterField__c ? `[${fields.SBQQ__FilterField__c}=${fields.SBQQ__FilterValue__c}]` : ''
        }`.trim();
      case 'lookupQuery': // Note: key in data is lookupQuery
        return `Lookup: ${fields.SBQQ__TestedObject__c}.${fields.SBQQ__TestedField__c} ${
          fields.SBQQ__Operator__c || ''
        } ${
          fields.SBQQ__MatchType__c === 'Static Value' ? `"${fields.SBQQ__TestedValue__c}"` : '(Field Value)'
        }`.trim();
      case 'errorConditions': // Note: key in data is errorConditions
        return `Condition: ${item.testedVariableReference || fields.SBQQ__TestedObject__c} ${
          fields.SBQQ__Operator__c || ''
        } ${
          fields.SBQQ__FilterValue__c !== null && fields.SBQQ__FilterValue__c !== undefined
            ? `"${fields.SBQQ__FilterValue__c}"`
            : item.filterVariableReference
        }`.trim();
      default:
        return innerItem.name || innerItem.sfId || item.id || 'Unknown Item';
    }
  };

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};

    productData.forEach((productDiff) => {
      const generatedRuleRows = { new: [], changed: [], deleted: [], same: [] };

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.productRules[type] || []).forEach((rule) => {
          const ruleData = {
            id: rule.sfId || rule.id,
            rule: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {rule.name || rule.sfId}
              </ArgonTypography>
            ),
            diffs: rule.changes, // Diffs of the rule itself
            sObjectName: 'SBQQ__ProductRule__c', // <--- SOBJECTNAME FOR RULE
            productActionsData: { new: [], changed: [], deleted: [], same: [] },
            lookupQueriesData: { new: [], changed: [], deleted: [], same: [] },
            errorConditionsData: { new: [], changed: [], deleted: [], same: [] },
            type: type,
          };

          // Process Product Actions
          if (rule.productActions) {
            ['new', 'changed', 'deleted', 'same'].forEach((actionType) => {
              (rule.productActions[actionType] || []).forEach((action) => {
                const innerAction = action.productActions || action;
                ruleData.productActionsData[actionType].push({
                  id: innerAction.sfId || innerAction.id || `action-${Math.random()}`, // Ensure unique ID
                  display: getNestedItemDisplayName(action, 'productActions'),
                  sObjectName: 'SBQQ__ProductAction__c', // <--- SOBJECTNAME FOR ACTION
                  diffs: action.changes,
                  type: actionType,
                });
              });
            });
          }

          // Process Lookup Queries
          if (rule.lookupQueries) {
            ['new', 'changed', 'deleted', 'same'].forEach((lookupType) => {
              (rule.lookupQueries[lookupType] || []).forEach((lookup) => {
                const innerLookup = lookup.lookupQuery || lookup;
                ruleData.lookupQueriesData[lookupType].push({
                  id: innerLookup.sfId || innerLookup.id || `lookup-${Math.random()}`, // Ensure unique ID
                  display: getNestedItemDisplayName(lookup, 'lookupQuery'),
                  sObjectName: 'SBQQ__LookupQuery__c', // <--- SOBJECTNAME FOR LOOKUP QUERY
                  diffs: lookup.changes,
                  type: lookupType,
                });
              });
            });
          }

          // Process Error Conditions
          if (rule.errorConditions) {
            ['new', 'changed', 'deleted', 'same'].forEach((conditionType) => {
              (rule.errorConditions[conditionType] || []).forEach((condition) => {
                const innerCondition = condition.errorConditions || condition;
                ruleData.errorConditionsData[conditionType].push({
                  id: innerCondition.sfId || innerCondition.id || `condition-${Math.random()}`, // Ensure unique ID
                  display: getNestedItemDisplayName(condition, 'errorConditions'),
                  sObjectName: 'SBQQ__ErrorCondition__c', // <--- SOBJECTNAME FOR ERROR CONDITION
                  diffs: condition.changes,
                  type: conditionType,
                });
              });
            });
          }

          generatedRuleRows[type].push(ruleData);
        });
      });

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id || productDiff.product.external_id] = {
        ruleRows: generatedRuleRows,
        productName: productDiff.product.name,
      };
    });

    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0 && !selectedProductId) {
      // Set initial selection only if not already set
      setSelectedProductId(
        productData[0].product.sfId || productData[0].product.id || productData[0].product.external_id
      );
    }
  }, [productData, selectedProductId]); // Add selectedProductId dependency to avoid resetting selection

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
  };

  const handleNestedItemClick = (itemId) => {
    setExpandedNestedItems((prevExpanded) => ({
      ...prevExpanded,
      [itemId]: !prevExpanded[itemId],
    }));
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setExpandedRow(null); // Collapse main rule when product changes
    setExpandedNestedItems({}); // Collapse nested items when product changes
  };

  // Renders a row for a nested item (action, lookup, condition)
  const renderNestedItemRow = (item) => {
    const isExpanded = expandedNestedItems[item.id];
    return (
      <React.Fragment key={item.id}>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell style={{ padding: '4px 8px', width: '100%' }} colSpan={2}>
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => handleNestedItemClick(item.id)}
              sx={{ cursor: 'pointer', width: '100%' }}
            >
              <ArgonTypography variant="caption" color="text" fontWeight="medium" sx={{ flexGrow: 1 }}>
                {item.display}
              </ArgonTypography>
              <IconButton
                aria-label="expand nested item"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNestedItemClick(item.id);
                }}
              >
                <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
              </IconButton>
            </ArgonBox>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ padding: '0px', borderBottom: 'none' }} colSpan={2}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <ArgonBox
                p={2}
                bgcolor={grey[100]}
                sx={{ border: `1px solid ${grey[200]}`, borderRadius: '4px', margin: '4px 0' }}
              >
                <DiffRenderer
                  changes={item.diffs}
                  sObjectName={item.sObjectName}
                  orgType={selectedOrg.type.toLowerCase()}
                  showSameFields={showSameFields}
                />
              </ArgonBox>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  // Renders the content inside the main expanded rule row
  const renderExpandedContent = (row) => {
    const hasActions = ['new', 'changed', 'deleted', 'same'].some((type) => row.productActionsData[type].length > 0);
    const hasLookups = ['new', 'changed', 'deleted', 'same'].some((type) => row.lookupQueriesData[type].length > 0);
    const hasConditions = ['new', 'changed', 'deleted', 'same'].some(
      (type) => row.errorConditionsData[type].length > 0
    );

    const renderNestedSection = (title, data) => {
      const hasData = ['new', 'changed', 'deleted', 'same'].some((type) => data[type].length > 0);
      if (!hasData) return null;

      return (
        <ArgonBox mt={2} mb={1}>
          <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1} color="textSecondary">
            {title}
          </ArgonTypography>
          <Table size="small">
            <TableBody>
              {['new', 'changed', 'deleted', 'same'].map(
                (type) =>
                  data[type].length > 0 && (
                    <React.Fragment key={type}>
                      <TableRow>
                        <TableCell colSpan={2} style={{ padding: '4px 8px', borderBottom: 'none' }}>
                          <ArgonTypography
                            variant="caption"
                            fontWeight="medium"
                            color="text"
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {type}
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                      {data[type].map((item) => renderNestedItemRow(item))}
                    </React.Fragment>
                  )
              )}
            </TableBody>
          </Table>
        </ArgonBox>
      );
    };

    return (
      <TableRow style={{ width: '100%', padding: '0px' }}>
        <TableCell colSpan={ruleColumns.length} style={{ padding: '0px', borderBottom: 'none' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
            <ArgonBox p={3} bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              <Divider sx={{ marginY: 2 }} />
              {renderNestedSection('Product Actions', row.productActionsData)}
              {renderNestedSection('Lookup Queries', row.lookupQueriesData)}
              {renderNestedSection('Error Conditions', row.errorConditionsData)}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderProductSection = (productName, productData) => {
    const { ruleRows } = productData;

    const renderTableSection = (title, rows, columns, renderRowSubComponent) => {
      if (!rows || rows.length === 0) {
        return null;
      }
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {renderRowSubComponent ? (
                  <StageExpandableTable
                    rows={rows}
                    columns={columns}
                    renderRowSubComponent={({ row }) => renderRowSubComponent(row)}
                  />
                ) : (
                  <StageProductTable rows={rows} columns={columns} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) => {
      return rows.map((row) => ({
        ...row,
        renderExpandedContent: () => renderExpandedContent(row),
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
    };

    const hasRuleData = ['new', 'changed', 'deleted', 'same'].some((type) => ruleRows[type].length > 0);

    if (!hasRuleData) {
      return (
        <ArgonBox key={productName} mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                No product rule data available for this product.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {['new', 'changed', 'deleted', 'same'].map(
          (type) =>
            ruleRows[type] &&
            ruleRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Product Rules`,
              formattedRows(ruleRows[type]),
              ruleColumns,
              (row) => row.renderExpandedContent() // Pass the function to render expanded content
            )
        )}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : productList.length > 0 ? (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  Select a product to view its rules.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          ) : (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  No products with rule data to display.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

// Updated PropTypes
ProductRulesReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      productRules: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object), // Allow null/undefined arrays
        changed: PropTypes.arrayOf(PropTypes.object),
        deleted: PropTypes.arrayOf(PropTypes.object),
        same: PropTypes.arrayOf(PropTypes.object),
      }).isRequired, // productRules itself is required
      product: PropTypes.shape({
        name: PropTypes.string,
        sf_id: PropTypes.string,
        external_id: PropTypes.string,
        id: PropTypes.string,
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool,
};

ProductRulesReview.defaultProps = {
  showSameFields: false, // Default value for showSameFields
};

export default ProductRulesReview;
